import type { CityTarget } from "./cities.ts";

// Generated from user-provided state -> cities mapping. Coordinates omitted (will rely on text search queries).
// Optional enhancement: pre-geocode and store lat/lng for each city to enable Nearby Search.

function slugifyCity(city: string, stateCode: string): string {
  return `${city}-${stateCode}`
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

// Mapping of state name to USPS code for cities that only supply full state names.
const STATE_CODE_LOOKUP: Record<string, string> = {
  "New York": "NY",
  California: "CA",
  Illinois: "IL",
  Texas: "TX",
  Pennsylvania: "PA",
  Arizona: "AZ",
  Florida: "FL",
  Indiana: "IN",
  Ohio: "OH",
  "North Carolina": "NC",
  Michigan: "MI",
  Tennessee: "TN",
  Massachusetts: "MA",
  Washington: "WA",
  Colorado: "CO",
  "District of Columbia": "DC",
  Maryland: "MD",
  Kentucky: "KY",
  Oregon: "OR",
  Oklahoma: "OK",
  Wisconsin: "WI",
  Nevada: "NV",
  "New Mexico": "NM",
  Missouri: "MO",
  Virginia: "VA",
  Georgia: "GA",
  Nebraska: "NE",
  Minnesota: "MN",
  Kansas: "KS",
  Louisiana: "LA",
  Hawaii: "HI",
  Alaska: "AK",
  "New Jersey": "NJ",
  Idaho: "ID",
  Alabama: "AL",
  Iowa: "IA",
  Arkansas: "AR",
  Utah: "UT",
  "Rhode Island": "RI",
  Mississippi: "MS",
  "South Dakota": "SD",
  Connecticut: "CT",
  "South Carolina": "SC",
  "New Hampshire": "NH",
  "North Dakota": "ND",
  Montana: "MT",
  Delaware: "DE",
  Maine: "ME",
  Wyoming: "WY",
  "West Virginia": "WV",
  Vermont: "VT",
};

// Original dataset mapping states to city arrays (trimmed formatting for clarity)
const RAW_STATE_CITIES: Record<string, string[]> = {
  // Oregon: ["Portland", "Eugene", "Salem"],
  // "New York": ["New York City", "Buffalo", "Rochester"],
  // California: ["Los Angeles", "San Francisco", "San Diego"],
  // Illinois: ["Chicago", "Springfield", "Aurora"],
  // Texas: ["Houston", "Dallas", "Austin"],
  // Pennsylvania: ["Philadelphia", "Pittsburgh", "Harrisburg"],
  // Arizona: ["Phoenix", "Tucson", "Mesa"],
  // Florida: ["Miami", "Orlando", "Tampa"],
  // Indiana: ["Indianapolis", "Fort Wayne", "Evansville"],
  // Ohio: ["Columbus", "Cleveland", "Cincinnati"],
  // "North Carolina": ["Charlotte", "Raleigh", "Greensboro"],
  // Michigan: ["Detroit", "Grand Rapids", "Ann Arbor"],
  // Tennessee: ["Nashville", "Memphis", "Knoxville"],
  // Massachusetts: ["Boston", "Worcester", "Cambridge"],
  // Washington: ["Seattle", "Spokane", "Tacoma"],
  // Colorado: ["Denver", "Colorado Springs", "Aurora"],
  // "District of Columbia": ["Washington"],
  // Maryland: ["Baltimore", "Annapolis", "Frederick"],
  // Kentucky: ["Louisville", "Lexington", "Bowling Green"],
  // Oklahoma: ["Oklahoma City", "Tulsa", "Norman"],
  // Wisconsin: ["Milwaukee", "Madison", "Green Bay"],
  // Nevada: ["Las Vegas", "Reno", "Henderson"],
  // "New Mexico": ["Albuquerque", "Santa Fe", "Las Cruces"],
  // Missouri: ["Kansas City", "St. Louis", "Springfield"],
  // Virginia: ["Richmond", "Virginia Beach", "Norfolk"],
  // Georgia: ["Atlanta", "Savannah", "Augusta"],
  // Nebraska: ["Omaha", "Lincoln", "Grand Island"],
  Minnesota: ["Minneapolis", "St. Paul", "Rochester"],
  Kansas: ["Wichita", "Overland Park", "Kansas City"],
  Louisiana: ["New Orleans", "Baton Rouge", "Lafayette"],
  Hawaii: ["Honolulu"],
  Alaska: ["Anchorage"],
  "New Jersey": ["Newark", "Jersey City", "Trenton"],
  Idaho: ["Boise", "Nampa", "Idaho Falls"],
  Alabama: ["Birmingham", "Montgomery", "Mobile"],
  Iowa: ["Des Moines", "Cedar Rapids", "Davenport"],
  Arkansas: ["Little Rock", "Fayetteville", "Fort Smith"],
  Utah: ["Salt Lake City", "Provo", "West Valley City"],
  "Rhode Island": ["Providence", "Warwick"],
  Mississippi: ["Jackson", "Gulfport", "Biloxi"],
  "South Dakota": ["Sioux Falls", "Rapid City"],
  Connecticut: ["Hartford", "New Haven", "Bridgeport"],
  "South Carolina": ["Columbia", "Charleston", "Greenville"],
  "New Hampshire": ["Manchester", "Nashua", "Concord"],
  "North Dakota": ["Fargo", "Bismarck", "Grand Forks"],
  Montana: ["Billings", "Missoula", "Bozeman"],
  Delaware: ["Wilmington", "Dover"],
  Maine: ["Portland", "Augusta"],
  Wyoming: ["Cheyenne", "Casper"],
  "West Virginia": ["Charleston", "Huntington", "Morgantown"],
  Vermont: ["Burlington", "Montpelier"],
};

export const ALL_US_CITIES: CityTarget[] = Object.entries(
  RAW_STATE_CITIES
).flatMap(([stateName, cities]) => {
  const stateCode = STATE_CODE_LOOKUP[stateName] || stateName;
  return cities.map((city) => ({
    name: `${city}, ${stateCode}`,
    slug: slugifyCity(city, stateCode),
  }));
});

// Utility to chunk cities for ingestion batching.
export function chunkCities(list: CityTarget[], size = 25): CityTarget[][] {
  const chunks: CityTarget[][] = [];
  for (let i = 0; i < list.length; i += size)
    chunks.push(list.slice(i, i + size));
  return chunks;
}
